import LegalLayout from "@/pages/legal/LegalLayout";

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Use"
      eyebrow="Legal"
      lastUpdated="February 1, 2026"
      testid="terms-page"
    >
      <p>
        These Terms of Use (the <strong>"Terms"</strong>) govern your access to and use of the ChurchFlow web
        application, websites, and related services (collectively, the <strong>"Service"</strong>) operated by{" "}
        <strong>Ministry Branch</strong>, a California-based organization (<strong>"Ministry Branch,"</strong>{" "}
        <strong>"we,"</strong> <strong>"our,"</strong> or <strong>"us"</strong>). ChurchFlow is part of the
        Ministry Branch family of services available at ministrybranch.com.
      </p>
      <p>
        By creating an account, accessing, or using the Service, you agree to be bound by these Terms. If you do
        not agree, do not use the Service.
      </p>

      <h2>1. Eligibility & Accounts</h2>
      <p>
        You must be at least 18 years old and have authority to bind the organization (church, ministry, or
        nonprofit) on whose behalf you register. You agree to provide accurate information, keep credentials
        confidential, and promptly notify us at <a href="mailto:security@ministrybranch.com">security@ministrybranch.com</a>{" "}
        of any unauthorized access.
      </p>

      <h2>2. The Service</h2>
      <p>
        ChurchFlow provides bookkeeping tools for churches and nonprofits including transaction tracking,
        donor records, recurring schedules, financial reports, and donor giving statements. The Service is{" "}
        <strong>not</strong> a substitute for professional accounting, tax, or legal advice. You are responsible
        for verifying that records produced through ChurchFlow comply with applicable accounting standards,
        IRS rules (including but not limited to substantiation requirements under IRC § 170(f)(8)), and any
        denominational or state-specific reporting requirements.
      </p>

      <h2>3. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful, fraudulent, or harmful purpose;</li>
        <li>Upload viruses, malicious code, or attempt to gain unauthorized access to any part of the Service or its underlying infrastructure;</li>
        <li>Reverse engineer, decompile, scrape, or copy the Service except as expressly permitted by law;</li>
        <li>Resell, sublicense, or use the Service to build a competing product;</li>
        <li>Submit personal data of donors or members without proper authorization or, where required, consent.</li>
      </ul>

      <h2>4. Your Data</h2>
      <p>
        You retain all right, title, and interest in the data you submit (<strong>"Customer Data"</strong>). You
        grant Ministry Branch a limited, worldwide, non-exclusive license to host, process, transmit, and display
        Customer Data solely as necessary to provide and improve the Service. Our handling of personal data is
        described in our <a href="/legal/privacy">Privacy Policy</a>.
      </p>

      <h2>5. Fees</h2>
      <p>
        Where the Service or specific features are offered for a fee, the applicable plan, price, and billing
        cycle will be presented to you before purchase. Fees are non-refundable except as required by law.
        We may change prices upon at least 30 days' notice for renewals.
      </p>

      <h2>6. Intellectual Property</h2>
      <p>
        Ministry Branch and its licensors own all right, title, and interest in and to the Service, including
        all software, design, trademarks, and logos (including <strong>"ChurchFlow"</strong> and the ChurchFlow
        logo). No rights are granted to you other than the limited right to use the Service per these Terms.
      </p>

      <h2>7. Third-Party Services</h2>
      <p>
        The Service may integrate with third-party providers (e.g., payment processors, email delivery, cloud
        hosting). Your use of those services is governed by their own terms; we are not responsible for them.
      </p>

      <h2>8. Termination</h2>
      <p>
        You may terminate your account at any time. We may suspend or terminate your access if you violate these
        Terms, fail to pay fees, or if we discontinue the Service. Upon termination, your right to use the
        Service ceases; you may export your Customer Data for a reasonable period (typically 30 days) prior to
        deletion.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED <strong>"AS IS"</strong> AND <strong>"AS AVAILABLE"</strong> WITHOUT WARRANTIES OF
        ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
        NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, error-free, or that data
        will never be lost.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, MINISTRY BRANCH SHALL NOT BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL,
        ARISING OUT OF OR RELATING TO THE SERVICE. OUR AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THESE
        TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE EVENT, OR
        (B) ONE HUNDRED U.S. DOLLARS ($100).
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You will indemnify and hold harmless Ministry Branch and its affiliates from any claims, losses, or
        damages (including reasonable attorneys' fees) arising from (a) your Customer Data, (b) your use of the
        Service, or (c) your violation of these Terms or applicable law.
      </p>

      <h2>12. Governing Law & Venue</h2>
      <p>
        These Terms are governed by the laws of the <strong>State of California</strong>, without regard to its
        conflict-of-laws principles. The exclusive venue for any dispute arising out of or relating to these
        Terms or the Service shall be the state or federal courts located in <strong>Fresno County, California</strong>,
        and you consent to personal jurisdiction in those courts.
      </p>

      <h2>13. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be communicated by email or in-app
        notice at least 14 days before they take effect. Continued use of the Service after the effective date
        constitutes acceptance.
      </p>

      <h2>14. Contact</h2>
      <p>
        Ministry Branch · Fresno, California · USA<br />
        <a href="mailto:legal@ministrybranch.com">legal@ministrybranch.com</a><br />
        <a href="https://ministrybranch.com" target="_blank" rel="noopener noreferrer">ministrybranch.com</a>
      </p>
    </LegalLayout>
  );
}
